/* @flow */
import * as React from 'react';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Checkbox from '@material-ui/core/Checkbox';
import Paper from '@material-ui/core/Paper';
import { Column, Table } from 'react-virtualized';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { ReactAccessConsumer } from 'react-access';
import { RouterStore } from 'mobx-react-router';

import ActionsList from 'components/ActionsList/ActionsList';
import type { Action } from 'components/ActionsList/ActionsList';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import type { DataStoreEntry } from 'stores/ReportViewStore';

import type {
  DataViewConfig,
  SortSpec,
  SelectionDetails,
  DataViewColumn,
  DataViewActions,
} from '../ReportView/types';
import OptionalTooltip from './OptionalTooltip';
import styles from './DataTable.module.scss';
import 'react-virtualized/styles.css';

export type TableHeaderProps = {
  selection: SelectionDetails,
  dataViewColumn: DataViewColumn,
  actions?: Action[],
  sort?: SortSpec,
  disableSort?: boolean,
  isFirst?: boolean,

  onActionCompleted?: Function,
};

const TABLE_SCROLL_BOUNDARY = 1280;

type InjectedTableHeaderProps = TranslatorProps;

@translate()
@observer
class TableHeader extends ComponentWithInjectedProps<
  TableHeaderProps,
  {},
  InjectedTableHeaderProps
> {
  @computed get selectedCount() {
    return this.props.selection.selectedItems.length;
  }

  renderActions() {
    const {
      selection: { selectedItems },
      actions,
      onActionCompleted,
    } = this.props;

    return (
      <span
        className={styles['action-buttons']}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <ActionsList
          data={selectedItems}
          actions={actions}
          tableHeader
          onActionCompleted={onActionCompleted}
        />
      </span>
    );
  }

  renderActionHeader() {
    const { t } = this.props;

    return (
      <div className={styles['action-buttons']}>
        <span onClick={(e: SyntheticInputEvent<*>) => e.stopPropagation()}>
          {this.props.t('general:labels.item_selected', { count: this.selectedCount })}
        </span>
        {this.renderActions()}
      </div>
    );
  }

  renderSortIcon() {
    const { sort, dataViewColumn, disableSort } = this.props;

    if (disableSort) {
      return null;
    }

    if (sort && sort.columnName === dataViewColumn.columnName) {
      return (
        <svg>
          <use
            xlinkHref={sort.direction === 'ASC' ? '#icon-sort-ascending' : '#icon-sort-descending'}
          />
        </svg>
      );
    }

    return (
      <svg
        className={!dataViewColumn.disableSort ? styles.showSortIcon : styles.sortAvailabilityIcon}
      >
        <use xlinkHref={'#icon-sort'} />
      </svg>
    );
  }

  render() {
    const { dataViewColumn, isFirst, t } = this.props;

    // This should never be the case
    if (!dataViewColumn.title) {
      return null;
    }

    if (this.selectedCount === 0) {
      const content = dataViewColumn.toolTip ? t(dataViewColumn.toolTip) : undefined;

      return (
        <div className={styles.tableHeaderWrapper}>
          <OptionalTooltip content={content}>
            <span>{t(dataViewColumn.title)}</span>
          </OptionalTooltip>
          {this.renderSortIcon()}
        </div>
      );
    }

    return isFirst ? (
      this.renderActionHeader()
    ) : (
      <div
        onClick={(e: SyntheticInputEvent<*>) => {
          e.stopPropagation();
        }}
        style={{ cursor: 'pointer' }}
      />
    );
  }
}

@observer
@translate()
class DataCell extends React.Component<*> {
  render(): ?React.Element<*> {
    const { t, cellData, dataKey, rowData, column } = this.props;

    if (typeof column.component === 'function') {
      try {
        // eslint-disable-next-line
        const els = column.component({
          display: cellData,
          field: dataKey,
          rowData: rowData,
          t,
        });
        // eslint-disable-next-line
        if (els === undefined) {
          // console.log(toJS(this.props.column));
        }

        return <span>{els}</span>;
      } catch (e) {
        console.error(e);
        return <span>--</span>;
      }
    }

    return null;
  }
}

type TableCheckBoxProps = {
  rowData: Object,
  viewConfig: DataViewConfig,
  selection: SelectionDetails,
  onCheckboxChange: (rowData: *, newValue: boolean) => void,
};

@observer
class TableCheckBox extends React.Component<TableCheckBoxProps> {
  render() {
    const { viewConfig, selection, onCheckboxChange, rowData } = this.props;
    const isChecked = selection.selectedItems.some(item => {
      return item[viewConfig.idKey] === rowData[viewConfig.idKey];
    });

    return (
      <Checkbox
        checked={isChecked}
        onChange={(_e, newValue: boolean) => onCheckboxChange(rowData, newValue)}
      />
    );
  }
}

type TableCheckBoxAllProps = {
  selection: SelectionDetails,
  data: any[],
  disableSelectAll?: boolean,
};

@observer
class TableCheckBoxAll extends React.Component<TableCheckBoxAllProps> {
  @action
  handleCheckBoxClick = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { selection, data } = this.props;

    if (selection.selectedItems.length > 0) {
      selection.selectedItems = [];
    } else {
      selection.selectedItems = data;
    }

    e.stopPropagation();
  };

  render() {
    const { selection, disableSelectAll } = this.props;
    const selectedCount = selection.selectedItems.length;
    const isChecked = !!selectedCount;

    if (disableSelectAll) {
      return null;
    }

    return (
      <Checkbox
        className={isChecked ? styles['header-checked'] : null}
        checked={isChecked}
        onClick={this.handleCheckBoxClick}
        checkedIcon={
          <svg>
            <use xlinkHref="#icon-checkbox-indeterminate" />
          </svg>
        }
      />
    );
  }
}

export type DataTableProps = {
  dataViewConfig: DataViewConfig,
  sort?: SortSpec,
  dataStoreEntry: DataStoreEntry,
  selection: SelectionDetails,
  actions?: DataViewActions,
  disableSelectAll?: boolean,
  onRequestSortChange?: (sort?: SortSpec) => void,
  onActionCompleted?: Function,
};

type InjectedDataTableProps = {
  routing: RouterStore,
} & TranslatorProps;

@translate()
@inject('routing')
@observer
export default class DataTable extends ComponentWithInjectedProps<
  DataTableProps,
  {},
  InjectedDataTableProps
> {
  @observable headerTransform = {
    transform: 'translateX(0)',
  };

  @computed get actions(): Action[] {
    const { selection, actions } = this.props;

    if (actions) {
      if (typeof actions === 'function') {
        const resolvedActions = actions(selection.selectedItems);
        return resolvedActions;
      }

      if (selection.selectedItems.length < 1) {
        return [];
      } else if (selection.selectedItems.length > 1) {
        return actions.multi || [];
      } else {
        return actions.single || [];
      }
    }

    return [];
  }

  resolveAllowedActions = (authorizeAccess: Function) =>
    this.actions.filter(action => authorizeAccess(action.permissions));

  tableRef: Table;

  scroller;

  handleSort = (sortData: { sortDirection: string, sortBy: string }) => {
    const { onRequestSortChange } = this.props;

    if (onRequestSortChange) {
      if (sortData && ['ASC', 'DESC'].includes(sortData.sortDirection.toUpperCase())) {
        onRequestSortChange({
          columnName: sortData.sortBy,
          direction: sortData.sortDirection.toUpperCase(),
        });
      } else {
        onRequestSortChange();
      }
    }
  };

  componentDidMount() {
    this.scroller = document.getElementsByClassName('ReactVirtualized__Table__Grid');
    this.scroller[0].addEventListener('scroll', this.handleScroll);

    this.checkColumnsAndWarn();
  }

  componentWillUnmount() {
    this.scroller[0].removeEventListener('scroll', this.handleScroll);
  }

  checkColumnsAndWarn = () => {
    const { dataViewConfig } = this.props;

    const totalFlexBasis = dataViewConfig.columns
      .filter(column => column.noFlexShrink)
      .reduce((_totalFlexBasis, column) => _totalFlexBasis + column.flexBasis, 0);

    if (totalFlexBasis > TABLE_SCROLL_BOUNDARY) {
      console.warn(
        `Table max rigid flex basis (columns that have \`noFlexShrink: true\` and a non-zero flex basis) exceeded: ${totalFlexBasis}px > ${TABLE_SCROLL_BOUNDARY}px.`
      );
    }
  };

  @action
  handleScroll = (e: MouseEvent) => {
    const scrollLeft = e.target.scrollLeft;

    this.headerTransform.transform = `translateX(-${scrollLeft}px)`;
  };

  onCheckboxChange = (viewConfig: DataViewConfig) => (item: *, newValue: boolean) => {
    const { selection } = this.props;
    const existingIndex = selection.selectedItems.findIndex(
      selectedItem => selectedItem[viewConfig.idKey] === item[viewConfig.idKey]
    );

    if (newValue) {
      if (existingIndex < 0) {
        selection.selectedItems.push(item);
      }
    } else {
      if (existingIndex >= 0) {
        selection.selectedItems.splice(existingIndex, 1);
      }
    }
  };

  renderColumnElements = (allowedActions: Action[], authorizeAccess: Function) => {
    const { dataViewConfig, selection, sort, onActionCompleted, t } = this.props;

    return dataViewConfig.columns
      .filter(dataViewColumn => {
        // Filter out any column that is set to be hidden, or if the current
        // user does not have the proper permission to see them.
        if (dataViewColumn.hidden === true) {
          return false;
        }

        return !dataViewColumn.permissions || authorizeAccess(dataViewColumn.permissions);
      })
      .map((dataViewColumn, index) => {
        let cellRenderer: Function;

        if (dataViewColumn.component) {
          cellRenderer = props => {
            return <DataCell {...props} column={dataViewColumn} />;
          };
        } else if (!dataViewColumn.columnName) {
          return <span style={{ color: 'red' }}>Not Supplied</span>;
        }

        const disableSort = dataViewColumn.disableSort;

        return (
          <Column
            cellRenderer={cellRenderer}
            headerRenderer={() => {
              return (
                <TableHeader
                  actions={allowedActions}
                  dataViewColumn={dataViewColumn}
                  selection={selection}
                  sort={sort}
                  disableSort={disableSort}
                  isFirst={index === 0}
                  onActionCompleted={onActionCompleted}
                />
              );
            }}
            disableSort={disableSort}
            key={`table-column-${dataViewColumn.columnName}`}
            label={t(dataViewColumn.title)}
            dataKey={dataViewColumn.columnName}
            flexGrow={dataViewColumn.noFlexGrow ? 0 : 1}
            flexShrink={dataViewColumn.noFlexShrink ? 0 : 1}
            width={dataViewColumn.flexBasis}
          />
        );
      });
  };

  render() {
    const { t, sort, dataStoreEntry, dataViewConfig, selection, disableSelectAll } = this.props;

    return (
      <ReactAccessConsumer>
        {authorizeAccess => {
          const allowedActions = this.resolveAllowedActions(authorizeAccess);
          const hasActions = !!dataViewConfig.actions;

          return (
            <React.Fragment>
              <Paper elevation={1} className={styles['table-container']}>
                <AutoSizer className={styles['table-auto-resizer']}>
                  {({ width, height }) => (
                    <Table
                      ref={ref => {
                        this.tableRef = ref;
                      }}
                      tabIndex={null}
                      headerHeight={48}
                      width={width}
                      height={height}
                      rowHeight={42}
                      rowClassName={i => (i.index % 2 === 0 ? 'even-row' : 'odd-row')}
                      sort={this.handleSort}
                      sortDirection={sort && sort.direction}
                      sortBy={sort && sort.columnName}
                      rowCount={dataStoreEntry.data ? dataStoreEntry.data.length : 0}
                      rowGetter={({ index }) => dataStoreEntry.data && dataStoreEntry.data[index]}
                      headerRowRenderer={({ className, columns, style }) => (
                        <HeaderRowComponent
                          className={className}
                          columns={columns}
                          style={style}
                          headerTransform={this.headerTransform}
                        />
                      )}
                    >
                      {[
                        ...(hasActions
                          ? [
                              <Column
                                key="checkBox"
                                dataKey="id"
                                disableSort
                                flexGrow={0}
                                flexShrink={0}
                                width={88}
                                headerRenderer={() => {
                                  return (
                                    <TableCheckBoxAll
                                      disableSelectAll={disableSelectAll}
                                      selection={selection}
                                      data={dataStoreEntry.data || []}
                                    />
                                  );
                                }}
                                cellRenderer={({ rowData }) => (
                                  <TableCheckBox
                                    onCheckboxChange={this.onCheckboxChange(dataViewConfig)}
                                    rowData={rowData}
                                    selection={selection}
                                    viewConfig={dataViewConfig}
                                  />
                                )}
                              />,
                            ]
                          : []),
                        ...this.renderColumnElements(allowedActions, authorizeAccess),
                      ]}
                    </Table>
                  )}
                </AutoSizer>
              </Paper>
            </React.Fragment>
          );
        }}
      </ReactAccessConsumer>
    );
  }
}

@observer
class HeaderRowComponent extends React.Component<{
  style: any,
  columns: any,
  className: string,
  headerTransform: any,
}> {
  render() {
    const { style, columns, className, headerTransform } = this.props;
    return (
      <div className={className} role="row" style={{ ...style, ...headerTransform }}>
        {columns}
      </div>
    );
  }
}
